import { useEffect, useRef } from "react";
import { GOOGLE_CLIENT_ID } from "../../../actions/constant";

const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";

let gsiScriptPromise = null;

const loadGsiScript = () => {
    if (gsiScriptPromise) return gsiScriptPromise;

    gsiScriptPromise = new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) return resolve();

        const loaded = document.querySelector(`script[src="${GSI_SCRIPT_URL}"]`);
        const script = loaded || document.createElement("script");

        script.addEventListener("load", resolve);
        script.addEventListener("error", reject);

        if (!loaded) {
            script.src = GSI_SCRIPT_URL;
            script.async = true;
            script.defer = true;
            document.body.appendChild(script);
        }
    }).catch(ex => {
        gsiScriptPromise = null;
        throw ex;
    });

    return gsiScriptPromise;
};

// One Tap hands back a signed JWT, the profile claims sit in its payload.
export const decodeCredential = (credential) => {
    try {
        const payload = String(credential).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const json = window.atob(payload).split("").map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join("");
        return JSON.parse(decodeURIComponent(json));
    } catch (ex) {
        console.log(ex);
        return null;
    }
};

/**
 * Renders nothing, just shows Google's One Tap / FedCM prompt.
 * onSuccess gets ("google", profile, credential) where profile carries
 * the same sub / name / email / picture keys the OAuth popup flow returns.
 */
const GoogleOneTap = ({ onSuccess, disabled = false, context = "signin", autoSelect = false, cancelOnTapOutside = false }) => {
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;

    useEffect(() => {
        if (disabled) return;

        let unmounted = false;

        loadGsiScript().then(() => {
            if (unmounted || !window.google?.accounts?.id) return;

            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                context: context,
                auto_select: autoSelect,
                cancel_on_tap_outside: cancelOnTapOutside,
                use_fedcm_for_prompt: true,
                itp_support: true,
                callback: ({ credential }) => {
                    const profile = decodeCredential(credential);
                    if (profile && profile.email) onSuccessRef.current("google", profile, credential);
                }
            });

            window.google.accounts.id.prompt();
        }).catch(ex => console.log(ex));

        return () => {
            unmounted = true;
            if (window.google?.accounts?.id) window.google.accounts.id.cancel();
        };
    }, [disabled, context, autoSelect, cancelOnTapOutside]);

    return null;
};

export default GoogleOneTap;
